import type { CourseDetail } from '@eliadmin/core/src/api/courses/coursers-api';
import { getCourseExternalUrlInput } from '../course-external-link';

/** API names differ from the visible form controls; never hide a rejected date/price. */
export function getCourseFormErrorField(field: string) {
  const fields: Record<string, string> = {
    price_with_vat: 'price',
    short_description: 'content',
    date_start: 'startDate',
    date_stop: 'endDate',
  };
  if (field.startsWith('categories.')) return 'categories';
  if (field.startsWith('metadata.')) return field.slice('metadata.'.length);
  return fields[field] ?? field;
}

export type CourseCategoryOptionSource = {
  id: number;
  name: string;
  childs?: CourseCategoryOptionSource[];
};

type CourseUpdatePreservationSource = Partial<
  Pick<
    CourseDetail,
    | 'duration'
    | 'time_start'
    | 'time_stop'
    | 'max_capacity'
    | 'course_type'
    | 'images'
    | 'attachments'
    | 'lecturers'
    | 'categories'
    | 'metadata'
  >
> & {
  // The store Course DetailResource returns one room; core types currently
  // describe an unrelated rooms collection instead.
  room?: { id: number } | null;
};

export function getPreservedCourseUpdateFields(
  course: CourseUpdatePreservationSource,
  selectedCategory: string,
  categoryChanged: boolean,
) {
  const toId = ({ id }: { id: number }) => ({ id });
  const primaryCategoryId = Number(selectedCategory);
  const categories = categoryChanged
    ? [
        { id: primaryCategoryId },
        ...(
          course.categories?.slice(1).filter(category => category.id !== primaryCategoryId) ?? []
        ).map(toId),
      ]
    : course.categories?.map(toId);

  // Whitelist only raw fields shared by the store DetailResource and
  // CourseRequest. Product availability/quantity are computed storefront
  // values; VAT, levels, and customer-group settings are not exposed here.
  // Forwarding the full response would also send lifecycle/ownership fields.
  // Category/lecturer lists can be availability-filtered: retaining returned
  // IDs is not a substitute for a complete owner edit API or PATCH semantics.
  return {
    duration: course.duration,
    time_start: course.time_start,
    time_stop: course.time_stop,
    max_capacity: course.max_capacity,
    course_type: course.course_type == null ? course.course_type : toId(course.course_type),
    room: course.room == null ? course.room : toId(course.room),
    images: course.images?.map(toId),
    attachments: course.attachments?.map(toId),
    lecturers: course.lecturers?.map(toId),
    categories,
    metadata: course.metadata,
  };
}

export function getCourseCategoryOptions(
  categories: CourseCategoryOptionSource[] = [],
  selected: CourseCategoryOptionSource[] = [],
) {
  const options = new Map<string, { value: string; label: string }>();
  const visit = (items: CourseCategoryOptionSource[]) => {
    for (const category of items) {
      const value = String(category.id);
      options.set(value, { value, label: category.name });
      if (category.childs) visit(category.childs);
    }
  };
  // The saved option must be available before the catalogue query resolves, and
  // even when it falls outside that query's current page.
  visit(selected);
  visit(categories);
  return Array.from(options.values());
}

const courseTypes: Record<string, string> = {
  '1': 'Doplnkový kurz',
  '2': 'Akreditovaný kurz',
  '3': 'Rekvalifikačný kurz',
};

export function getEditCourseValues(course: CourseDetail) {
  const metadata = course.metadata ?? {};
  const instructors = Array.isArray(metadata.instructors)
    ? metadata.instructors.filter(Boolean)
    : [];
  return {
    name: course.name ?? '',
    image: course.image ?? null,
    description: course.description ?? '',
    content: course.short_description ?? '',
    targetAudience: metadata.targetAudience ?? '',
    confirmationForm: metadata.confirmationForm ?? [],
    learningMethod: metadata.learningMethod ?? [],
    instructors: instructors.length ? instructors : [{ name: '' }],
    courseType: Array.isArray(metadata.courseType)
      ? metadata.courseType
          .map(type => {
            const value = type?.name ?? type?.id;
            return value == null ? '' : (courseTypes[String(value)] ?? String(value));
          })
          .filter(Boolean)
      : [],
    categories: course.categories?.[0]?.id?.toString() ?? '',
    startDate: course.date_start ? new Date(course.date_start) : null,
    endDate: course.date_stop ? new Date(course.date_stop) : null,
    price: (
      course.product?.pricing?.list?.with_vat ??
      course.product?.pricing?.retail?.with_vat ??
      course.product?.price_with_vat ??
      ''
    ).toString(),
    externalUrl: getCourseExternalUrlInput(course),
    canBeReimbursed: metadata.canBeReimbursed ?? false,
    requiresExam: metadata.requiresExam ?? false,
    courseLocation: metadata.courseLocation ?? '',
  };
}
